import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TournamentV2, Tournament } from '../types';
import './TournamentEditModal.css';

interface TournamentEditModalProps {
  tournament: TournamentV2 | Tournament;
  isOpen: boolean;
  onClose: () => void;
  onSave: (tournamentId: number, updates: TournamentUpdateData) => Promise<void>;
}

export interface TournamentUpdateData {
  name: string;
  description: string;
  start_date: string;
  end_date?: string;
  status: 'upcoming' | 'active' | 'completed' | 'cancelled';
}

const TournamentEditModal: React.FC<TournamentEditModalProps> = ({
  tournament,
  isOpen,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  
  // Helper to get participant count from either V1 or V2
  const getParticipantCount = () => {
    if ('settings' in tournament) {
      // V2 tournament
      return tournament.settings?.participant_count || 16;
    } else {
      // V1 tournament
      return (tournament as Tournament).max_participants || 16;
    }
  };
  
  const [formData, setFormData] = useState<TournamentUpdateData>({
    name: tournament.name,
    description: tournament.description || '',
    start_date: tournament.start_date.split('T')[0],
    end_date: tournament.end_date ? tournament.end_date.split('T')[0] : '',
    status: tournament.status,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update form when tournament changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: tournament.name,
        description: tournament.description || '',
        start_date: tournament.start_date.split('T')[0],
        end_date: tournament.end_date ? tournament.end_date.split('T')[0] : '',
        status: tournament.status,
      });
      setError(null);
    }
  }, [tournament, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      await onSave(tournament.id, formData);
      onClose();
    } catch (err) {
      console.error('Error updating tournament:', err);
      setError(err instanceof Error ? err.message : t('tournaments.edit.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original values
    setFormData({
      name: tournament.name,
      description: tournament.description || '',
      start_date: tournament.start_date.split('T')[0],
      end_date: tournament.end_date ? tournament.end_date.split('T')[0] : '',
      status: tournament.status,
    });
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content tournament-edit-modal">
        <div className="modal-header">
          <h3>{t('tournaments.edit.title')}</h3>
          <button onClick={handleCancel} className="close-button">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="name">{t('tournaments.edit.name')} *</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              maxLength={100}
              placeholder={t('tournaments.edit.namePlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">{t('tournaments.edit.description')}</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              maxLength={500}
              placeholder={t('tournaments.edit.descriptionPlaceholder')}
            />
            <small className="form-help">
              {formData.description.length}/500 {t('tournaments.edit.characters')}
            </small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="start_date">{t('tournaments.edit.startDate')} *</label>
              <input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="end_date">{t('tournaments.edit.endDate')}</label>
              <input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                min={formData.start_date}
              />
              <small className="form-help">{t('tournaments.edit.endDateHelp')}</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="status">{t('tournaments.edit.status')}</label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => setFormData({ 
                ...formData, 
                status: e.target.value as TournamentUpdateData['status']
              })}
            >
              <option value="upcoming">{t('tournaments.status.upcoming')}</option>
              <option value="active">{t('tournaments.status.active')}</option>
              <option value="completed">{t('tournaments.status.completed')}</option>
              <option value="cancelled">{t('tournaments.status.cancelled')}</option>
            </select>
            <small className="form-help">{t('tournaments.edit.statusHelp')}</small>
          </div>

          <div className="info-box">
            <strong>ℹ️ {t('tournaments.edit.infoTitle')}</strong>
            <p>{t('tournaments.edit.infoFormat')}: {t(`tournaments.format.${tournament.format}`)}</p>
            <p>{t('tournaments.edit.infoParticipants')}: {getParticipantCount()}</p>
            <p className="warning-text">{t('tournaments.edit.infoWarning')}</p>
          </div>

          <div className="modal-footer">
            <button 
              type="button" 
              onClick={handleCancel} 
              className="btn btn-secondary"
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TournamentEditModal;
